import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, LogIn, LogOut, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client/workspace';
import { formatDateInVenue, formatTimeInVenue } from '@/shared/i18n/formatDate';
import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { useEmployees } from './employees';
import { useCloseShift, useCorrectShift, useOpenShift, useShifts, type Shift } from './shifts';

export function shiftErrorMessage(
  t: ReturnType<typeof useTranslation>['t'],
  error: unknown,
): string {
  if (error instanceof ApiError && error.code) {
    const key = `shifts.errors.${error.code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return t('shifts.errors.generic');
}

// `datetime-local` works in wall-clock components; build them from the local
// browser time so the round-trip back through `new Date(value)` stays faithful.
export function toDateTimeLocal(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function ShiftsPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const employees = useEmployees();
  const [employeeId, setEmployeeId] = useState('');
  const [historyState, setHistoryState] = useState<'closed' | 'open'>('closed');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // The workspace fixes the Venue by URL (ADR 0015); its name/timezone ride `me.venues`,
  // so a Venue-scoped Operator without `venue.view` still resolves their venue.
  const selectedVenue = venues.find((venue) => venue.id === venueId);
  const effectiveVenueId = venueId;
  const scope = { kind: 'venue', venueId: effectiveVenueId } as const;
  const canManage = auth.can('workforce.shift.manage', scope);
  const canCorrect = auth.can('workforce.shift.correct', scope);
  const canView = auth.canAny(['workforce.shift.view', 'workforce.shift.manage'], scope);

  const openShifts = useShifts({ venueId: effectiveVenueId, state: 'open' }, canView);
  const history = useShifts(
    {
      venueId: effectiveVenueId,
      state: historyState,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
    },
    canView,
  );
  const openShift = useOpenShift();
  const closeShift = useCloseShift();
  const correct = useCorrectShift();

  if (employees.isLoading) {
    return <section className="p-6 text-muted-foreground">{t('shifts.loading')}</section>;
  }

  if (!canView) return <Forbidden />;

  const activeEmployees = employees.data?.items.filter((employee) => employee.active) ?? [];

  async function submitOpen(event: React.FormEvent) {
    event.preventDefault();
    if (!effectiveVenueId || !employeeId) return;
    try {
      await openShift.mutateAsync({ venueId: effectiveVenueId, employeeId });
      setEmployeeId('');
      toast.success(t('shifts.toast.opened'));
    } catch (caught) {
      toast.error(shiftErrorMessage(t, caught));
    }
  }

  async function submitClose(shift: Shift) {
    try {
      await closeShift.mutateAsync({ venueId: shift.venueId, shiftId: shift.id });
      toast.success(t('shifts.toast.closed'));
    } catch (caught) {
      toast.error(shiftErrorMessage(t, caught));
    }
  }

  async function submitCorrect(shift: Shift, edit: { openedAt?: string; closedAt?: string }) {
    try {
      await correct.mutateAsync({
        venueId: shift.venueId,
        shiftId: shift.id,
        openedAt: edit.openedAt,
        // An open shift never gains a closedAt through a correction (ADR 0016).
        closedAt: shift.open ? undefined : edit.closedAt,
      });
      toast.success(t('shifts.toast.corrected'));
    } catch (caught) {
      toast.error(shiftErrorMessage(t, caught));
      throw caught;
    }
  }

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('shifts.title')}</h1>
      </div>

      {canManage && (
        <form
          onSubmit={submitOpen}
          className="flex flex-wrap items-end gap-3 rounded-md border p-4"
        >
          <div className="grid min-w-64 gap-1">
            <Label htmlFor="shift-employee">{t('shifts.fields.employee')}</Label>
            <select
              id="shift-employee"
              required
              className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              value={employeeId}
              onChange={(event) => setEmployeeId(event.target.value)}
            >
              <option value="">{t('shifts.open.selectEmployee')}</option>
              {activeEmployees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={openShift.isPending || !employeeId}>
            <LogIn className="size-4" aria-hidden="true" />
            {t('shifts.actions.open')}
          </Button>
        </form>
      )}

      <ShiftTable
        title={t('shifts.current.title')}
        icon={Clock}
        shifts={openShifts.data?.items ?? []}
        loading={openShifts.isLoading}
        empty={t('shifts.current.empty')}
        selectedTimezone={selectedVenue?.timezone}
        closeLabel={t('shifts.actions.close')}
        onClose={canManage ? submitClose : undefined}
        onCorrect={canCorrect ? submitCorrect : undefined}
      />

      <div className="grid gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="mr-auto text-lg font-semibold">{t('shifts.history.title')}</h2>
          <div className="grid gap-1">
            <Label htmlFor="shift-state">{t('shifts.fields.state')}</Label>
            <select
              id="shift-state"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              value={historyState}
              onChange={(event) => setHistoryState(event.target.value as 'closed' | 'open')}
            >
              <option value="closed">{t('shifts.state.closed')}</option>
              <option value="open">{t('shifts.state.open')}</option>
            </select>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="shift-from">{t('shifts.fields.from')}</Label>
            <Input
              id="shift-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="shift-to">{t('shifts.fields.to')}</Label>
            <Input id="shift-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <ShiftTable
          shifts={history.data?.items ?? []}
          loading={history.isLoading}
          empty={t('shifts.history.empty')}
          selectedTimezone={selectedVenue?.timezone}
          closeLabel={t('shifts.actions.close')}
          onClose={canManage ? submitClose : undefined}
          onCorrect={canCorrect ? submitCorrect : undefined}
        />
      </div>
    </section>
  );
}

function ShiftTable({
  title,
  icon: Icon,
  shifts,
  loading,
  empty,
  selectedTimezone,
  closeLabel,
  onClose,
  onCorrect,
}: {
  title?: string;
  icon?: typeof Clock;
  shifts: Shift[];
  loading: boolean;
  empty: string;
  selectedTimezone?: string;
  closeLabel: string;
  onClose?: (shift: Shift) => void;
  onCorrect?: (shift: Shift, edit: { openedAt?: string; closedAt?: string }) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const locale = { locale: 'en-US', timezone: selectedTimezone ?? 'UTC' };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOpenedAt, setEditOpenedAt] = useState('');
  const [editClosedAt, setEditClosedAt] = useState('');

  function startEdit(shift: Shift) {
    setEditingId(shift.id);
    setEditOpenedAt(toDateTimeLocal(shift.openedAt));
    setEditClosedAt(shift.closedAt ? toDateTimeLocal(shift.closedAt) : '');
  }

  async function saveEdit(shift: Shift) {
    if (!onCorrect) return;
    try {
      await onCorrect(shift, {
        openedAt: editOpenedAt ? new Date(editOpenedAt).toISOString() : undefined,
        closedAt: !shift.open && editClosedAt ? new Date(editClosedAt).toISOString() : undefined,
      });
      setEditingId(null);
    } catch {
      // Keep the correction form open so the user can fix the rejected values;
      // submitCorrect already surfaced the error as a toast.
    }
  }

  return (
    <div className="grid gap-3">
      {title && (
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          {Icon && <Icon className="size-5" aria-hidden="true" />}
          {title}
        </h2>
      )}
      {loading ? (
        <p className="text-muted-foreground">{t('shifts.loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('shifts.columns.employee')}</th>
                <th className="px-4 py-2 font-medium">{t('shifts.columns.venue')}</th>
                <th className="px-4 py-2 font-medium">{t('shifts.columns.opened')}</th>
                <th className="px-4 py-2 font-medium">{t('shifts.columns.closed')}</th>
                <th className="px-4 py-2 font-medium">{t('shifts.columns.actors')}</th>
                <th className="px-4 py-2 font-medium">{t('shifts.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {shifts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    {empty}
                  </td>
                </tr>
              )}
              {shifts.map((shift) => (
                <Fragment key={shift.id}>
                  <tr className="border-b last:border-0">
                    <td className="px-4 py-2 font-medium">{shift.employeeName}</td>
                    <td className="px-4 py-2">{shift.venueName}</td>
                    <td className="px-4 py-2">
                      {formatDateInVenue(shift.openedAt, locale)}{' '}
                      {formatTimeInVenue(shift.openedAt, locale)}
                    </td>
                    <td className="px-4 py-2">
                      {shift.closedAt
                        ? `${formatDateInVenue(shift.closedAt, locale)} ${formatTimeInVenue(
                            shift.closedAt,
                            locale,
                          )}`
                        : t('shifts.state.open')}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {shift.openedByUserLabel}
                      {shift.closedByUserLabel ? ` / ${shift.closedByUserLabel}` : ''}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        {shift.open && onClose ? (
                          <Button size="sm" variant="outline" onClick={() => onClose(shift)}>
                            <LogOut className="size-4" aria-hidden="true" />
                            {closeLabel}
                          </Button>
                        ) : (
                          !onCorrect && (
                            <span className="text-muted-foreground">
                              {t('shifts.state.closed')}
                            </span>
                          )
                        )}
                        {onCorrect && editingId !== shift.id && (
                          <Button size="sm" variant="ghost" onClick={() => startEdit(shift)}>
                            <Pencil className="size-4" aria-hidden="true" />
                            {t('shifts.actions.correct')}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {onCorrect && editingId === shift.id && (
                    <tr className="border-b last:border-0 bg-muted/30">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="grid gap-1">
                            <Label htmlFor={`correct-opened-${shift.id}`}>
                              {t('shifts.correct.openedAt')}
                            </Label>
                            <Input
                              id={`correct-opened-${shift.id}`}
                              type="datetime-local"
                              aria-label={t('shifts.correct.openedAt')}
                              value={editOpenedAt}
                              onChange={(e) => setEditOpenedAt(e.target.value)}
                            />
                          </div>
                          {!shift.open && (
                            <div className="grid gap-1">
                              <Label htmlFor={`correct-closed-${shift.id}`}>
                                {t('shifts.correct.closedAt')}
                              </Label>
                              <Input
                                id={`correct-closed-${shift.id}`}
                                type="datetime-local"
                                aria-label={t('shifts.correct.closedAt')}
                                value={editClosedAt}
                                onChange={(e) => setEditClosedAt(e.target.value)}
                              />
                            </div>
                          )}
                          <Button size="sm" onClick={() => saveEdit(shift)}>
                            {t('shifts.actions.saveCorrection')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                            {t('shifts.actions.cancel')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
